import {
    format,
    parse,
    parseISO,
    addMonths,
    addDays,
    getDaysInMonth,
    getDate,
    type Locale,
} from 'date-fns';
import { enUS, da, nb, fi, fr, ptBR } from 'date-fns/locale';

const DATE_FNS_LOCALE_MAP: Record<string, Locale> = {
    en: enUS,
    da,
    no: nb,
    fi,
    fr,
    'pt-BR': ptBR,
    'pt-PT': ptBR,
};

// Maps an i18next language code (e.g. "da", "pt-BR") to the corresponding
// date-fns locale. Falls back to enUS if the language isn't mapped.
export const getDateFnsLocale = (language: string): Locale => {
    return DATE_FNS_LOCALE_MAP[language] ?? enUS;
};

// Formats a Date as "YYYY-MM-DD"
export const toDateInputValue = (d: Date): string => format(d, 'yyyy-MM-dd');

// Returns the current month as "YYYY-MM"
export const getCurrentMonth = (): string => format(new Date(), 'yyyy-MM');

// Shifts a "YYYY-MM" month string by delta months (positive or negative)
export const shiftMonth = (month: string, delta: number): string => {
    const date = parse(month, 'yyyy-MM', new Date());
    return format(addMonths(date, delta), 'yyyy-MM');
};

// Formats a "YYYY-MM" string as e.g. "March 2026"
export const formatMonthLabel = (month: string): string => {
    const date = parse(month, 'yyyy-MM', new Date());
    return format(date, 'MMMM yyyy');
};

// Formats a date as "Jan 1, 2026"
export const formatShortDate = (date: string | Date): string =>
    format(new Date(date), 'MMM d, yyyy');

// Formats a calendar date (date-only string or UTC-midnight timestamp) as
// "Jan 1, 2026". Parses only the date part so the day never shifts with the
// viewer's timezone — use for start/end/effective dates, not real timestamps.
export const formatShortCalendarDate = (date: string): string =>
    format(parseISO(date.slice(0, 10)), 'MMM d, yyyy');

export const formatIsoWeekRange = (
    weekKey: string,
    language: string
): string => {
    const [year, week] = weekKey.split('-').map(Number);
    const januaryFourth = new Date(year, 0, 4, 12);
    const daysSinceMonday = (januaryFourth.getDay() + 6) % 7;
    const startDate = addDays(
        januaryFourth,
        -daysSinceMonday + (week - 1) * 7
    );
    const endDate = addDays(startDate, 6);
    const locale = getDateFnsLocale(language);

    if (startDate.getMonth() === endDate.getMonth()) {
        return `${format(startDate, 'MMM d', { locale })}–${format(
            endDate,
            'd',
            { locale }
        )}`;
    }

    return `${format(startDate, 'MMM d', { locale })}–${format(
        endDate,
        'MMM d',
        { locale }
    )}`;
};

// Converts a "YYYY-MM" string to a start/end date range (inclusive, "YYYY-MM-DD")
export const monthToDateRange = (
    month: string
): { start: string; end: string } => {
    const date = parse(month, 'yyyy-MM', new Date());
    const lastDay = getDaysInMonth(date);
    const start = format(date, 'yyyy-MM-dd');
    const end = `${month}-${String(lastDay).padStart(2, '0')}`;
    return { start, end };
};

// Returns the current day-of-month and total days in the current month (UTC)
export const getCurrentMonthProgress = (): {
    dayOfMonth: number;
    daysInMonth: number;
} => {
    const now = new Date();
    return {
        dayOfMonth: getDate(now),
        daysInMonth: getDaysInMonth(now),
    };
};
